import { Bookmark, ChevronDown, Search, SlidersHorizontal, Trash2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cn } from "../../lib/cn";
import { activeFilterCount, EMPTY_STATE, loadViews, persistViews, secondaryCount, writeState, type BoardState, type SavedView } from "../../lib/board";
import type { ProjectOverview } from "../../lib/types";
import { Button } from "../ui/button";
import { FilterSelect } from "../ui/primitives";

interface Props {
  state: BoardState;
  projects: ProjectOverview[];
  update: (p: Partial<BoardState>) => void;
  reset: (keep?: Partial<BoardState>) => void;
  applyView: (params: string) => void;
  mobile: boolean;
}

const repoLabel = (full: string) => full.split("/").pop() ?? full;

/** Búsqueda y filtros principales (proyecto, repositorio) a la vista; el resto, en «Más filtros». */
export function BoardFilters({ state, projects, update, reset, applyView, mobile }: Props) {
  const [q, setQ] = useState(state.q);
  const [more, setMore] = useState(false);
  useEffect(() => { setQ(state.q); }, [state.q]);
  useEffect(() => { // la búsqueda se envía al servidor al dejar de escribir
    if (q === state.q) return;
    const t = setTimeout(() => update({ q }), 300);
    return () => clearTimeout(t);
  }, [q, state.q, update]);

  const repos = projects.filter((p) => !state.project || p.id === state.project);
  const sec = secondaryCount(state);
  const filtered = activeFilterCount(state);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <label className="relative min-w-[200px] flex-1 basis-[240px] md:max-w-[340px]">
          <span className="sr-only">Buscar por identificador o título</span>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-mu" aria-hidden />
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por ID o título…" maxLength={100}
            className="h-11 w-full rounded-[10px] border border-ln2 bg-sf pl-9 pr-3 text-[14px] text-tx placeholder:text-mu/70" />
        </label>
        <FilterSelect aria-label="Proyecto" value={state.project} className={cn("max-w-[200px]", state.project && "border-ac")}
          onChange={(e) => update({ project: e.target.value, repo: "" })}>
          <option value="">Proyecto: todos</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </FilterSelect>
        <FilterSelect aria-label="Repositorio" value={state.repo} className={cn("max-w-[220px]", state.repo && "border-ac")} onChange={(e) => update({ repo: e.target.value })}>
          <option value="">Repositorio: todos</option>
          {repos.map((p) => (
            <optgroup key={p.id} label={p.name}>{p.repositories.map((r) => <option key={r.id} value={r.id}>{repoLabel(r.fullName)}</option>)}</optgroup>
          ))}
        </FilterSelect>
        <Button variant="secondary" onClick={() => setMore((v) => !v)} aria-expanded={more} aria-controls="board-more-filters" className={cn("px-3.5", sec > 0 && "border-ac")}>
          <SlidersHorizontal className="h-4 w-4" />Más filtros{sec > 0 && <span className="rounded-full bg-ac px-1.5 font-mono text-[11px] text-acx">{sec}</span>}
          <ChevronDown className={cn("h-4 w-4 transition-transform", more && "rotate-180")} />
        </Button>
        <SavedViews state={state} applyView={applyView} mobile={mobile} />
        {filtered > 0 && <Button variant="ghost" onClick={() => reset({ scope: state.scope, view: state.view })} className="px-3"><X className="h-4 w-4" />Limpiar ({filtered})</Button>}
      </div>
      {more && (
        <div id="board-more-filters" className="flex flex-wrap items-center gap-2 rounded-card border border-ln bg-sf p-3">
          <FilterSelect aria-label="Prioridad" value={state.prio} onChange={(e) => update({ prio: e.target.value })}>
            <option value="">Prioridad: todas</option><option value="high">Alta</option><option value="medium">Media</option><option value="low">Baja</option>
          </FilterSelect>
          <FilterSelect aria-label="Riesgo" value={state.risk} onChange={(e) => update({ risk: e.target.value })}>
            <option value="">Riesgo: todos</option><option value="minimal">Mínimo</option><option value="standard">Estándar</option><option value="high">Alto</option><option value="unknown">Desconocido</option>
          </FilterSelect>
          <FilterSelect aria-label="Flujo de aprobación" value={state.flow} onChange={(e) => update({ flow: e.target.value })}>
            <option value="">Flujo: todos</option><option value="fast_track">FAST TRACK</option><option value="supervised">SUPERVISED</option><option value="restricted">RESTRICTED</option>
          </FilterSelect>
          <FilterSelect aria-label="Agente" value={state.agent} onChange={(e) => update({ agent: e.target.value as BoardState["agent"] })}>
            <option value="">Agente: cualquiera</option><option value="running">En ejecución</option><option value="idle">Sin run activo</option>
          </FilterSelect>
          <label className="flex h-11 cursor-pointer items-center gap-2 rounded-[10px] border border-ln2 px-3.5 text-[14px] font-medium">
            <input type="checkbox" checked={state.mine} onChange={(e) => update({ mine: e.target.checked })} className="h-4 w-4 accent-[var(--ac)]" />Solo mis misiones
          </label>
        </div>
      )}
    </div>
  );
}

/** Vistas guardadas: combinaciones de filtros y vista con nombre. Viven en este navegador (no se comparten entre personas ni dispositivos). */
function SavedViews({ state, applyView, mobile }: { state: BoardState; applyView: (p: string) => void; mobile: boolean }) {
  const [open, setOpen] = useState(false);
  const [views, setViews] = useState<SavedView[]>(loadViews);
  const [name, setName] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const off = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", off); document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", off); document.removeEventListener("keydown", esc); };
  }, [open]);

  const save = () => {
    const n = name.trim().slice(0, 40);
    if (!n) return;
    const next = [...views.filter((v) => v.name.toLowerCase() !== n.toLowerCase()), { id: crypto.randomUUID(), name: n, params: writeState({ ...state, col: "" }).toString() }].slice(-12);
    setViews(next); persistViews(next); setName("");
  };
  const remove = (id: string) => { const next = views.filter((v) => v.id !== id); setViews(next); persistViews(next); };
  const dirty = JSON.stringify(state) !== JSON.stringify(EMPTY_STATE);

  return (
    <div ref={ref} className="relative">
      <Button variant="secondary" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-haspopup="dialog" className="px-3.5"><Bookmark className="h-4 w-4" />Vistas{views.length > 0 && <span className="font-mono text-[12px] text-mu">{views.length}</span>}</Button>
      {open && (
        <div role="dialog" aria-label="Vistas guardadas" className={cn("absolute z-30 mt-1.5 w-[300px] rounded-card border border-ln2 bg-sf p-3 shadow-xl", mobile ? "left-0" : "right-0")}>
          {views.length === 0 && <p className="m-0 mb-2 text-[13px] text-mu">Aún no hay vistas. Ajusta filtros y guárdalos con un nombre.</p>}
          <ul className="m-0 mb-2 flex list-none flex-col gap-1 p-0">
            {views.map((v) => (
              <li key={v.id} className="flex items-center gap-1">
                <button className="min-h-10 flex-1 cursor-pointer truncate rounded-lg px-2.5 text-left text-[14px] hover:bg-hv" onClick={() => { applyView(v.params); setOpen(false); }}>{v.name}</button>
                <button aria-label={`Borrar la vista ${v.name}`} className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg text-mu hover:text-er" onClick={() => remove(v.id)}><Trash2 className="h-4 w-4" /></button>
              </li>
            ))}
          </ul>
          <div className="flex gap-2 border-t border-ln pt-2.5">
            <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} placeholder="Nombre de la vista actual" maxLength={40} aria-label="Nombre de la vista"
              className="h-10 min-w-0 flex-1 rounded-lg border border-ln2 bg-bg px-2.5 text-[14px] text-tx" />
            <Button size="sm" variant="primary" onClick={save} disabled={!name.trim() || !dirty} title={dirty ? undefined : "Aplica algún filtro primero"}>Guardar</Button>
          </div>
          <p className="m-0 mt-2 text-[12px] text-mu">Se guardan en este navegador. La empresa se elige aparte, arriba.</p>
        </div>
      )}
    </div>
  );
}
